import { useMemo, useState, type ReactElement } from 'react';
import { Link2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { usePermissions } from '@/auth/usePermissions';
import { Button } from '@/components/ui/button';
import { PageWithSidebar } from '@/components/shell/PageWithSidebar';
import { BrowseNavSidebar } from '@/components/shell/BrowseNavSidebar';
import { BrowseFamiliesPanel } from '@/features/families/components/BrowseFamiliesPanel';
import { BrowseFamiliesSkeleton } from '@/features/families/components/BrowseFamiliesSkeleton';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import {
  RECENT_FAMILIES_DEFAULT_LIMIT,
  RECENT_FAMILIES_PAGE_SIZE,
  useRecentFamilies,
} from '@/hooks/useRecentFamilies';
import {
  nextFamilySort,
  parseFamilySortKey,
  parseSortDirection,
  sortFamilySummaries,
  type FamilySortKey,
} from '@/features/families/lib/sortFamilySummaries';

export function BrowseFamiliesPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canCreateFamilies } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit, setLimit] = useState(RECENT_FAMILIES_DEFAULT_LIMIT);
  const query = useRecentFamilies({ limit });

  // GET /families exposes both limit and offset, but the FE uses progressive
  // limit growth to keep prior rows visible during a load-more — same pattern
  // as BrowsePatientsPage.
  const hasMore = (query.data?.length ?? 0) >= limit;
  const isLoadingMore = query.isFetching && !query.isPending;
  const loadMore = (): void => {
    setLimit((current) => current + RECENT_FAMILIES_PAGE_SIZE);
  };

  const q = searchParams.get('q') ?? '';
  const sortKey = parseFamilySortKey(searchParams.get('sort'));
  const sortDir = parseSortDirection(searchParams.get('dir')) ?? 'asc';

  function setQ(val: string): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (val.length > 0) {
          next.set('q', val);
        } else {
          next.delete('q');
        }
        return next;
      },
      { replace: true },
    );
  }

  const rows = useMemo(
    () => (query.isSuccess ? query.data : []),
    [query.isSuccess, query.data],
  );

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (term.length === 0) return rows;
    return rows.filter((f) => {
      const name = f.name.toLowerCase();
      const guid = f.guid.toLowerCase();
      return name.includes(term) || guid.includes(term);
    });
  }, [rows, q]);

  const sorted = useMemo(
    () => sortFamilySummaries(filtered, sortKey, sortDir),
    [filtered, sortKey, sortDir],
  );

  function onSortChange(key: FamilySortKey): void {
    const next = nextFamilySort({ key: sortKey, direction: sortDir }, key);
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next.key === null) {
          params.delete('sort');
          params.delete('dir');
        } else {
          params.set('sort', next.key);
          params.set('dir', next.direction);
        }
        return params;
      },
      { replace: true },
    );
  }

  return (
    <PageWithSidebar sidebar={<BrowseNavSidebar />}>
      <div className="sticky top-0 z-20 border-b border-border bg-card px-8 py-3 flex items-center gap-3">
        <Link2
          className="h-[18px] w-[18px] text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <h1 className="text-[15px] font-semibold tracking-tight text-foreground">
          {t('routes.browseFamilies.heading')}
        </h1>
        <div className="ml-auto flex items-center gap-3">
          {query.isSuccess && (
            <span className="text-[12px] text-muted-foreground tabular-nums">
              {filtered.length === rows.length
                ? `${rows.length.toLocaleString()}${hasMore ? '+' : ''} ${t('routes.browseFamilies.countLabel')}`
                : `${filtered.length.toLocaleString()} ${t('routes.browseFamilies.countOf')} ${rows.length.toLocaleString()}${hasMore ? '+' : ''}`}
            </span>
          )}
          {query.isSuccess && hasMore && (
            <Button
              variant="outline"
              size="sm"
              onClick={loadMore}
              loading={isLoadingMore}
              loadingText={t('routes.browseFamilies.loadingMore')}
              className="h-7 text-xs"
            >
              {t('routes.browseFamilies.loadMore')}
            </Button>
          )}
          {canCreateFamilies && (
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                navigate('/families/new');
              }}
            >
              <Plus className="me-1 h-3.5 w-3.5" aria-hidden="true" />
              {t('routes.browseFamilies.newFamily')}
            </Button>
          )}
        </div>
      </div>

      <div className="px-8 py-5 space-y-4 bg-background">
        {query.isPending && <BrowseFamiliesSkeleton />}

        {query.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={query.error}
              heading={t('routes.browseFamilies.error.heading')}
              message={t('routes.browseFamilies.error.message')}
              codeLabel={t('routes.browseFamilies.error.codeLabel')}
              onRetry={() => {
                query.refetch().catch(/* noop */ () => {
                  /* intentional */
                });
              }}
              retryLabel={t('routes.browseFamilies.error.retry')}
            />
          </div>
        )}

        {query.isSuccess && (
          <BrowseFamiliesPanel
            q={q}
            onQueryChange={setQ}
            families={sorted}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={onSortChange}
          />
        )}
      </div>
    </PageWithSidebar>
  );
}
